/**
 * User Profile component for header
 */

import React, { useState } from 'react';
import { useAuth } from '@/hooks';
import { cn } from '@/utils';

interface UserProfileProps {
  className?: string;
}

export function UserProfile({ className }: UserProfileProps) {
  const { user, isLoading, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  if (isLoading) {
    return (
      <div className={cn('user-profile', className)}>
        <div className="user-avatar animate-pulse">...</div>
        <div className="user-info">
          <div className="user-name">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const initials = user.name
    ? user.name
        .split(' ')
        .map(n => n.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user.email.charAt(0).toUpperCase();

  const displayRole = user.role 
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
    : 'User';

  const handleLogout = async () => {
    try {
      setIsDropdownOpen(false);
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className={cn('relative', className)}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="user-profile hover:opacity-80 transition-opacity"
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
      >
        <div className="user-avatar">
          {initials}
        </div>
        <div className="user-info">
          <div className="user-name">
            {user.name || user.email.split('@')[0]}
          </div>
          <div className="user-role">
            {displayRole}
          </div>
        </div>
        <svg
          className={cn(
            'w-4 h-4 ml-2 transition-transform duration-200',
            isDropdownOpen && 'rotate-180'
          )}
          style={{ color: 'var(--access-neutral-lt-60)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isDropdownOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsDropdownOpen(false)}
          />
          
          {/* Dropdown Menu */}
          <div 
            className="absolute right-0 top-full mt-2 w-64 rounded-lg shadow-lg border py-2 z-20"
            style={{ 
              backgroundColor: 'var(--access-neutral-lt-00)',
              borderColor: 'var(--access-neutral-lt-30)',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
            }}
          >
            <div 
              className="px-4 py-3 border-b"
              style={{ borderColor: 'var(--access-neutral-lt-30)' }}
            >
              <div 
                className="text-sm font-medium mb-1"
                style={{ color: 'var(--access-neutral-lt-90)' }}
              >
                {user.name || 'User'}
              </div>
              <div 
                className="text-sm break-words"
                style={{ color: 'var(--access-neutral-lt-60)' }}
              >
                {user.email}
              </div>
              <div 
                className="text-xs mt-1"
                style={{ color: 'var(--access-neutral-lt-60)' }}
              >
                Role: {displayRole}
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm transition-colors flex items-center space-x-2 hover:bg-red-50"
              style={{ color: 'var(--access-error)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Sign Out</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
